import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { Account, CardStatus } from '@zeusm/personal/core/entities';
import { buildPersonalApiUrl } from '@zeusm/shared/core/helpers';

import { CardsApi } from './cards-api.interface';
import {
  CardCloseReason,
  CardProduct,
  CardProductsRes,
  CreateCardReq,
  CreateCardRes,
  GetSecureCardDetailsRes,
  ReissueCardReq,
  ReissueCardRes,
} from '../entities/cards';

@Injectable()
export class CardsApiService implements CardsApi {
  readonly #http = inject(HttpClient);

  getProducts(): Observable<CardProductsRes> {
    return this.#http.get<CardProductsRes>(buildPersonalApiUrl('card-products/'));
  }

  getProduct(id: string): Observable<CardProduct> {
    return this.#http.get<CardProduct>(buildPersonalApiUrl(`card-products/${id}/`));
  }

  create(body: CreateCardReq): Observable<CreateCardRes> {
    return this.#http.post<CreateCardRes>(buildPersonalApiUrl('cards/'), body);
  }

  getSecureCardDetails(id: string): Observable<GetSecureCardDetailsRes> {
    return this.#http.get<GetSecureCardDetailsRes>(buildPersonalApiUrl(`cards/${id}/secure/`));
  }

  reissueCard(params: ReissueCardReq): Observable<ReissueCardRes> {
    const { phone, cardId } = params;
    return this.#http.post<ReissueCardRes>(buildPersonalApiUrl(`cards/${cardId}/reissue/`), { phone });
  }

  blockCard(id: string): Observable<Account> {
    return this.#updateStatus(id, CardStatus.Blocked);
  }

  unblockCard(id: string): Observable<Account> {
    return this.#updateStatus(id, CardStatus.Active);
  }

  closeCard(id: string, reason?: CardCloseReason, otherReason?: string, targetAccountID?: string): Observable<Account> {
    return this.#updateStatus(id, CardStatus.Closed, reason, otherReason, targetAccountID);
  }

  #updateStatus(id: string, status: CardStatus, reason?: string, otherReason?: string, targetAccountID?: string): Observable<Account> {
    return this.#http.patch<Account>(buildPersonalApiUrl(`cards/${id}/status/`), {
      cardId: id,
      targetAccountID: targetAccountID || null,
      status,
      reason,
      otherReason,
    });
  }
}
